<script lang="ts">
interface Props {
    label?: string;
    result?: string;
    description?: string;
    metaOne?: string;
    metaTwo?: string;
    metaThree?: string;
    image?: { src?: string; alt?: string };
    link?: string;
    hideBlock?: boolean;
}

let {
    label = 'Results',
    result = '',
    description = '',
    metaOne = '',
    metaTwo = '',
    metaThree = '',
    image = {},
    link = '',
    hideBlock = false,
}: Props = $props();

let hasImage = $derived(Boolean(image?.src));
</script>

{#if !hideBlock}
    {#if link}
        <a class:service-result-card--has-image={hasImage} class="service-result-card" href={link}>
            {#if image?.src}
                <img class="service-result-card__image" src={image.src} alt={image.alt ?? label} />
            {/if}
            <span class="service-result-card__label">{label}</span>
            <span class="service-result-card__body">
                <strong>{result}</strong>
                <span>{description}</span>
            </span>
            {#if metaOne || metaTwo || metaThree}
                <span class="service-result-card__meta">
                    {#if metaOne}<span>{metaOne}</span>{/if}
                    {#if metaOne && metaTwo}<span aria-hidden="true">•</span>{/if}
                    {#if metaTwo}<span>{metaTwo}</span>{/if}
                    {#if (metaOne || metaTwo) && metaThree}<span aria-hidden="true">•</span>{/if}
                    {#if metaThree}<span>{metaThree}</span>{/if}
                </span>
            {/if}
            <span class="service-result-card__arrow" aria-hidden="true"></span>
            <svg class="service-result-card__border" aria-hidden="true" focusable="false">
                <rect pathLength="1"></rect>
            </svg>
        </a>
    {:else}
        <div class:service-result-card--has-image={hasImage} class="service-result-card">
            {#if image?.src}
                <img class="service-result-card__image" src={image.src} alt={image.alt ?? label} />
            {/if}
            <span class="service-result-card__label">{label}</span>
            <span class="service-result-card__body">
                <strong>{result}</strong>
                <span>{description}</span>
            </span>
            {#if metaOne || metaTwo || metaThree}
                <span class="service-result-card__meta">
                    {#if metaOne}<span>{metaOne}</span>{/if}
                    {#if metaOne && metaTwo}<span aria-hidden="true">•</span>{/if}
                    {#if metaTwo}<span>{metaTwo}</span>{/if}
                    {#if (metaOne || metaTwo) && metaThree}<span aria-hidden="true">•</span>{/if}
                    {#if metaThree}<span>{metaThree}</span>{/if}
                </span>
            {/if}
            <span class="service-result-card__arrow" aria-hidden="true"></span>
        </div>
    {/if}
{/if}

<style lang="scss">
    @use 'sass-mq/mq' as *;

    .service-result-card {
        background: var(--colour-classic-chalk);
        border-radius: 0.8rem;
        color: var(--colour-dreamless-sleep);
        display: grid;
        gap: 0.8rem;
        overflow: hidden;
        padding: 1.6rem 4.4rem 1.6rem 2.4rem;
        position: relative;
        text-decoration: none;
    }

    a.service-result-card:focus-visible {
        outline: none;
    }

    .service-result-card__label {
        color: var(--colour-grey);
        font-size: 1rem;
        line-height: 1.2;
        text-transform: uppercase;
    }

    .service-result-card__image {
        display: none;
    }

    .service-result-card__body {
        align-items: center;
        display: flex;
        gap: 2.4rem;
    }

    strong {
        font-family: var(--font-secondary);
        font-size: 4.5rem;
        font-weight: 400;
        letter-spacing: -0.135rem;
        line-height: 1.4;
    }

    .service-result-card__body span {
        font-size: 1.4rem;
        line-height: 1.2;

        @include mq($from: tablet) {
            font-size: 1.6rem;
            line-height: 1.4;
        }
    }

    .service-result-card__meta {
        color: var(--colour-grey);
        display: flex;
        flex-wrap: wrap;
        font-size: 1.2rem;
        gap: 1.5rem;
        line-height: 1.5;
    }

    .service-result-card--has-image {
        @include mq($from: desktop) {
            align-items: center;
            column-gap: 1.6rem;
            grid-template-areas:
                'image body'
                'image meta';
            grid-template-columns: 9.9rem 1fr;
            row-gap: 0;
            min-height: 11.3rem;
            padding: 0 5.6rem 0 0;
        }
    }

    .service-result-card--has-image .service-result-card__image {
        @include mq($from: desktop) {
            display: block;
            grid-area: image;
            height: 100%;
            object-fit: contain;
            width: 9.9rem;
        }
    }

    .service-result-card--has-image .service-result-card__label {
        @include mq($from: desktop) {
            display: none;
        }
    }

    .service-result-card--has-image .service-result-card__body {
        @include mq($from: desktop) {
            grid-area: body;
        }
    }

    .service-result-card--has-image .service-result-card__meta {
        @include mq($from: desktop) {
            grid-area: meta;
        }
    }

    .service-result-card__arrow {
        align-items: center;
        display: flex;
        justify-content: center;
        position: absolute;
        right: 1.8rem;
        top: 50%;
        transform: translateY(-50%);

        &::before {
            background: url('/src/stories/assets/arrow-circle-right.svg') no-repeat center;
            background-size: 100% 100%;
            content: '';
            display: block;
            height: 1.9894rem;
            width: 2.0814rem;
        }
    }

    .service-result-card__border {
        display: block;
        height: 100%;
        inset: 0;
        pointer-events: none;
        position: absolute;
        width: 100%;
        z-index: 2;
    }

    .service-result-card__border rect {
        fill: none;
        height: calc(100% - 1px);
        rx: 0.8rem;
        ry: 0.8rem;
        opacity: 0;
        stroke: var(--colour-noghrei-silver);
        stroke-dasharray: 1;
        stroke-dashoffset: 1;
        stroke-width: 1px;
        transition:
            opacity 0.22s ease-out,
            stroke-dashoffset 0s linear 0.22s;
        vector-effect: non-scaling-stroke;
        width: calc(100% - 1px);
        x: 0.5px;
        y: 0.5px;
    }

    a.service-result-card:hover .service-result-card__border rect,
    a.service-result-card:focus-visible .service-result-card__border rect {
        opacity: 1;
        stroke-dashoffset: 0;
        transition:
            opacity 0s,
            stroke-dashoffset 0.44s cubic-bezier(0.22, 1, 0.36, 1);
    }

    @media (prefers-reduced-motion: reduce) {
        .service-result-card__border rect {
            transition-duration: 0s;
        }
    }
</style>
